import { useCallback, useEffect, useState } from 'react'

export interface NativeAuthUser {
  sub?: string
  username?: string
  email?: string
  name?: string
  preferredUsername?: string
  roles?: string[]
  [key: string]: any
}

export interface NativeAuthConfig {
  loginEndpoint: string
  meEndpoint: string
  logoutEndpoint: string
  autoRefresh?: boolean
}

export interface UseNativeAuthReturn {
  user: NativeAuthUser | null
  authenticated: boolean
  ready: boolean
  error: string | null
  login: (identifier?: string, password?: string) => Promise<boolean>
  logout: () => Promise<void>
  checkSession: () => Promise<boolean>
  refresh: () => Promise<boolean>
  handleCallback: () => Promise<boolean>
}

const DEFAULT_CONFIG: Partial<NativeAuthConfig> = {
  loginEndpoint: '/api/auth/login',
  meEndpoint: '/api/auth/me',
  logoutEndpoint: '/api/auth/logout',
  autoRefresh: true,
}

export function useNativeAuth(config?: Partial<NativeAuthConfig>): UseNativeAuthReturn {
  const finalConfig: NativeAuthConfig = {
    ...DEFAULT_CONFIG,
    ...config,
  } as NativeAuthConfig

  const [user, setUser] = useState<NativeAuthUser | null>(null)
  const [authenticated, setAuthenticated] = useState(false)
  const [ready, setReady] = useState(false) // Whether initial check is done
  const [error, setError] = useState<string | null>(null)

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(finalConfig.meEndpoint, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache',
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated) {
          setUser(data.user)
          setAuthenticated(true)
          return true
        }
      }
      
      setUser(null)
      setAuthenticated(false)
      return false
    } catch (err) {
      console.error('Session check failed:', err)
      setUser(null)
      setAuthenticated(false)
      return false
    } finally {
      setReady(true)
    }
  }, [finalConfig.meEndpoint])

  const login = useCallback(async (identifier?: string, password?: string) => {
    setError(null)

    if (!identifier || !password) {
      const msg = 'Benutzername und Passwort sind erforderlich'
      setError(msg)
      throw new Error(msg)
    }

    try {
      // API expects email or username based on format
      const isEmail = identifier.includes('@')
      const payload = isEmail
        ? { email: identifier, password }
        : { username: identifier, password }

      const response = await fetch(finalConfig.loginEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        throw new Error(errorData?.message || errorData?.detail || 'Ungültige Anmeldedaten')
      }

      const data = await response.json()
      if (data.authenticated && data.user) {
        setUser(data.user)
        setAuthenticated(true)
        return true
      }
      
      throw new Error('Login failed: Invalid response')
    } catch (err: any) {
      console.error('Login failed:', err)
      const msg = err.message || 'Login fehlgeschlagen. Bitte erneut versuchen.'
      setError(msg)
      throw new Error(msg)
    }
  }, [finalConfig.loginEndpoint])

  const logout = useCallback(async () => {
    try {
      await fetch(finalConfig.logoutEndpoint, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (err) {
      console.error('Logout failed:', err)
    } finally {
      setUser(null)
      setAuthenticated(false)
    }
  }, [finalConfig.logoutEndpoint])

  // Initial session check
  useEffect(() => {
    void checkSession()
  }, [checkSession])

  // Auto refresh session periodically if enabled
  useEffect(() => {
    if (!finalConfig.autoRefresh) {
      return
    }

    const interval = window.setInterval(() => {
      void checkSession()
    }, 5 * 60 * 1000) // every 5 minutes

    return () => window.clearInterval(interval)
  }, [finalConfig.autoRefresh, checkSession])

  return {
    user,
    authenticated,
    ready,
    error,
    login,
    logout,
    checkSession,
    refresh: checkSession,
    handleCallback: () => checkSession(), // legacy compat: verify actual session instead of always returning true
  }
}
